import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";

/* ===== API base ===== */
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL;

/* Minimal HTTP helper */
async function http(path, { method = "GET", data, headers, signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(headers || {}) },
    body: data ? JSON.stringify(data) : undefined,
    signal,
    credentials: "include",
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    throw new Error(json?.error || `HTTP ${res.status}`);
  }
  return json;
}

/* Local API */
const api = {
  list: (params = {}) => {
    const usp = new URLSearchParams(params);
    const qs = usp.toString() ? `?${usp.toString()}` : "";
    return http(`/api/feedbacks${qs}`);
  },
  remove: (id) => http(`/api/feedbacks/${id}`, { method: "DELETE" }),
};

const theme = {
  brand: "#3dccc7",
  brandDark: "#2a5a7a",
  text: "#f1f1f1",
  subtext: "#d6d9de",
  bg: "#0b1320",
  card: "#0f1b2d",
  border: "#1e293b",
};

const Page = styled.div`display: grid; gap: 16px; color: ${theme.text};`;
const Title = styled.h2`margin: 0; font-weight: 800;`;
const Row = styled.div`display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap;`;
const Card = styled.div`background: ${theme.card}; border: 1px solid ${theme.border}; border-radius: 14px; overflow: hidden;`;
const Button = styled.button`
  background: ${theme.brand}; color: #fff; border: 0; padding: 10px 14px; border-radius: 10px; font-weight: 700; cursor: pointer;
  &:hover{ background: ${theme.brandDark}; }
  &:disabled{ opacity: .6; cursor: not-allowed; }
`;
const Danger = styled(Button)`background: #b91c1c; &:hover{ background:#991b1b; }`;
const Table = styled.table`
  width: 100%; border-collapse: collapse; color: ${theme.text};
  th, td { border-bottom: 1px solid ${theme.border}; padding: 12px; text-align: left; vertical-align: top; }
  th { background: rgba(255,255,255,0.03); }
`;

const EmailCell = styled.span`
  color: #93c5fd; cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const Short = styled.div`
  max-width: 400px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; color: ${theme.subtext};
`;

const SearchRow = styled.div`display:flex; gap:10px; flex-wrap: wrap;`;
const Input = styled.input`
  padding: 10px 12px; border-radius: 10px; border: 1px solid ${theme.border};
  background: #0c1626; color: ${theme.text};
`;

export default function FeedbacksManager(){
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, limit: 50 });

  // ✅ Memoized loader (depends only on values it reads: q and meta.limit)
  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await api.list({ page: p, limit: meta.limit || 50, q: q || "" });
      setRows(res.data || []);
      setMeta({ total: res.total || 0, limit: res.limit || 50, page: res.page || p });
      setPage(res.page || p);
    } finally {
      setLoading(false);
    }
  }, [q, meta.limit]);

  // ✅ useEffect depends on memoized load
  useEffect(() => { load(1); }, [load]);

  function fmt(d){
    return d ? new Date(d).toLocaleString(undefined, {
      year:"numeric", month:"short", day:"numeric", hour:"2-digit", minute:"2-digit"
    }) : "";
  }

  async function copyEmail(email){
    try{
      await navigator.clipboard.writeText(email);
      alert("Email copied: " + email);
    } catch { alert("Failed to copy"); }
  }

  async function handleDelete(row){
    if (!window.confirm(`Delete feedback from: ${row.name}?`)) return;
    try { await api.remove(row._id); await load(page); }
    catch(e){ alert(e.message || "Failed to delete"); }
  }

  const totalPages = Math.max(1, Math.ceil((meta.total || 0) / (meta.limit || 50)));

  return (
    <Page>
      <Row>
        <Title>eBook Feedback</Title>
        <SearchRow>
          <Input
            placeholder="Search name, email or text…"
            value={q}
            onChange={(e)=>setQ(e.target.value)}
            onKeyDown={(e)=>{ if(e.key==='Enter') load(1); }}
          />
          <Button onClick={()=>load(1)}>Search</Button>
        </SearchRow>
      </Row>

      <Card>
        <Table>
          <thead>
            <tr>
              <th style={{width:40}}>#</th>
              <th>Name</th>
              <th>Email</th>
              <th>Valuable/Insightful</th>
              <th>Confused / Wanted More</th>
              <th>Confidence</th>
              <th>Submitted</th>
              <th style={{width:140}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} style={{padding:20}}>Loading…</td></tr>}
            {!loading && rows.length===0 && <tr><td colSpan={8} style={{padding:20}}>No feedback yet.</td></tr>}
            {!loading && rows.map((r, idx)=>(
              <tr key={r._id}>
                <td>{(meta.page-1)*(meta.limit||50)+idx+1}</td>
                <td>{r.name}</td>
                <td>
                  <EmailCell title="Click to copy" onClick={()=>copyEmail(r.email)}>{r.email}</EmailCell>
                </td>
                <td><Short title={r.valuable || ""}>{r.valuable || "—"}</Short></td>
                <td><Short title={r.confused || ""}>{r.confused || "—"}</Short></td>
                <td><Short title={r.confidence || ""}>{r.confidence || "—"}</Short></td>
                <td>{fmt(r.createdAt)}</td>
                <td>
                  <div style={{display:"flex", gap:8}}>
                    <Danger onClick={()=>handleDelete(r)}>Delete</Danger>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      {/* Simple pager */}
      <Row>
        <div style={{opacity:.8}}>Total: {meta.total}</div>
        <div style={{display:"flex", gap:8, alignItems:"center"}}>
          <Button onClick={()=>{ if(page>1){ load(page-1); } }} disabled={page<=1}>Prev</Button>
          <div>Page {page} / {totalPages}</div>
          <Button onClick={()=>{ if(page<totalPages){ load(page+1); } }} disabled={page>=totalPages}>Next</Button>
        </div>
      </Row>
    </Page>
  );
}
